import { PerformanceCard } from "@components/Cards/PerformanceCard";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { Tooltip, TooltipContent, TooltipTrigger } from "@tracify/ui/tooltip";
import { dashboardModeAtom } from "@/atoms";
import { PerformanceCardInterface } from "@/interface/PerformanceCardInterface";
import { useAtom } from "jotai";
import { useNewVsReturningState } from "@lib/hooks/url-state";
import React, { useCallback, useMemo } from "react";
import { CACDevelopmentChart } from "./CACDevelopmentChart";
import { RevenueInfoContainer } from "./RevenueInfoContainer";
import useCurrency from "../../../lib/hooks/use-currency";
import useFormat from "@lib/hooks/use-format";
import { getCorrelation } from "@components/NumberDisplays/calculations";
import { KpisOverviewResponse } from "@lib/api-hooks";
import {
  KpisPerChannelResponse,
  ChannelData,
} from "@lib/api-hooks/useKpisPerChannel";
import {
  generalMetricsInformation,
  metricsInformation,
} from "@components/MetricNameWithTooltip/metricsInformation";
import InfoIcon from "@tracify/ui/info-icon";
import { Granularity } from "@lib/api-hooks/useKpiBreakdownForChannel";
import { DailyMetric } from "@lib/api-hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@tracify/ui/card";
import { Skeleton } from "@tracify/ui/skeleton";
import { cn } from "@/lib/utils";

type Props = {
  data?: KpisOverviewResponse;
  comparedData?: KpisOverviewResponse;
  channelData?: KpisPerChannelResponse;
  comparedChannelData?: KpisPerChannelResponse;
  loading?: boolean;
  granularity?: Granularity;
};

/**
 * Helper function to get the correct data array based on granularity
 *
 * @param {KpisOverviewResponse | undefined} data - The KPI overview response data
 * @param {Granularity} granularity - The granularity level (daily, weekly, monthly)
 * @return {DailyMetric[]} The data array for the specified granularity
 */
const getGranularityData = (
  data: KpisOverviewResponse | undefined,
  granularity: Granularity
): DailyMetric[] => {
  if (!data) return [];

  switch (granularity) {
    case "weekly":
      return data.weekly ?? [];
    case "monthly":
      return data.monthly ?? [];
    case "daily":
    default:
      return data.daily ?? [];
  }
};

/**
 * Helper function to get the correct channel data array based on granularity
 *
 * @param {Record<string, ChannelData>} channelData - The channel data record
 * @param {Granularity} granularity - The granularity level (daily, weekly, monthly)
 * @return {Record<string, ChannelData>} The channel data with the correct granularity array
 */
const getGranularityChannelData = (
  channelData: Record<string, ChannelData>,
  granularity: Granularity
): Record<string, ChannelData> => {
  const result: Record<string, ChannelData> = {};

  Object.entries(channelData).forEach(([channelKey, channel]) => {
    let granularityData: DailyMetric[] | undefined;

    switch (granularity) {
      case "weekly":
        granularityData = channel.weekly;
        break;
      case "monthly":
        granularityData = channel.monthly;
        break;
      case "daily":
      default:
        granularityData = channel.daily;
        break;
    }

    result[channelKey] = {
      ...channel,
      daily: granularityData ?? [],
    };
  });

  return result;
};

/**
 * Implements the component for the marketing performance.
 *
 * @return {CustomElement}
 */
export function MarketingPerformance({
  data,
  loading,
  comparedData,
  channelData,
  comparedChannelData,
  granularity = "daily",
}: Props) {
  const { newVsReturning: nvrKey } = useNewVsReturningState();
  const [dashboardMode] = useAtom(dashboardModeAtom);
  const { formatNumber } = useFormat();
  const { calculateCurrencyConvertion } = useCurrency();

  // Get the appropriate metrics information based on dashboard mode
  const metricsInfo =
    dashboardMode === "general"
      ? generalMetricsInformation
      : metricsInformation;

  // Helper function to create tooltip if metric information exists
  const createTooltip = useCallback(
    (metricKey: string, customContent?: React.ReactNode) => {
      if (customContent) {
        return customContent;
      }

      const tooltipContent = metricsInfo[metricKey as keyof typeof metricsInfo];
      if (!tooltipContent) return null;

      return (
        <Tooltip>
          <TooltipTrigger>
            <InfoIcon />
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-lg">{tooltipContent}</p>
          </TooltipContent>
        </Tooltip>
      );
    },
    [metricsInfo]
  );

  const performanceCardsData = useMemo<PerformanceCardInterface[]>(() => {
    const cards = [
      {
        title: "Ad Spend",
        value: `${calculateCurrencyConvertion(
          data?.overall?.[nvrKey].total_spend,
          2
        )}`,
        comparedValue: `${calculateCurrencyConvertion(
          comparedData?.overall?.[nvrKey]?.total_spend,
          2
        )}`,
        compared: Boolean(comparedData),
        isIncreasing: Boolean(
          data?.overall?.[nvrKey].total_spend &&
          comparedData?.overall?.[nvrKey]?.total_spend &&
          data?.overall?.[nvrKey].total_spend >
            comparedData?.overall?.[nvrKey]?.total_spend
        ),
        rate: getCorrelation(
          data?.overall?.[nvrKey].total_spend ?? 0,
          comparedData?.overall?.[nvrKey]?.total_spend ?? 0,
          2
        ),
        dataAttributes: { display: "currency" },
        tooltip: createTooltip("total_spend"),
      },
      {
        title: dashboardMode === "general" ? "Conv. Value" : "Revenue",
        value: `${calculateCurrencyConvertion(
          data?.overall?.[nvrKey].customer_revenue,
          2
        )} `,
        comparedValue: `${calculateCurrencyConvertion(
          comparedData?.overall?.[nvrKey]?.customer_revenue,
          2
        )} `,
        compared: Boolean(comparedData),
        isIncreasing: Boolean(
          data?.overall?.[nvrKey].customer_revenue &&
          comparedData?.overall?.[nvrKey]?.customer_revenue &&
          data?.overall?.[nvrKey].customer_revenue >
            comparedData?.overall?.[nvrKey]?.customer_revenue
        ),
        rate:
          data?.overall?.[nvrKey].customer_revenue &&
          comparedData?.overall?.[nvrKey]?.customer_revenue
            ? getCorrelation(
                data?.overall?.[nvrKey].customer_revenue,
                comparedData?.overall?.[nvrKey]?.customer_revenue,
                2
              )
            : 0,
        dataAttributes: { display: "currency" },
        tooltip: createTooltip("marketingOverviewRevenue"),
      },
      {
        title: dashboardMode === "general" ? "CPA" : "CPO",
        value: `${calculateCurrencyConvertion(
          data?.overall?.[nvrKey].cost_per_order,
          2
        )} `,
        comparedValue: `${calculateCurrencyConvertion(
          comparedData?.overall?.[nvrKey]?.cost_per_order,
          2
        )} `,
        compared: Boolean(comparedData),
        isIncreasing: Boolean(
          data?.overall?.[nvrKey].cost_per_order &&
          comparedData?.overall?.[nvrKey]?.cost_per_order &&
          data?.overall?.[nvrKey].cost_per_order <
            comparedData?.overall?.[nvrKey]?.cost_per_order
        ),
        rate:
          data?.overall?.[nvrKey].cost_per_order &&
          comparedData?.overall?.[nvrKey]?.cost_per_order
            ? getCorrelation(
                data?.overall?.[nvrKey].cost_per_order,
                comparedData?.overall?.[nvrKey]?.cost_per_order,
                2
              )
            : 0,
        dataAttributes: { display: "currency" },
        tooltip: createTooltip("cost_per_order"),
      },
      {
        title: "MER",
        value: formatNumber(data?.overall?.[nvrKey].return_on_ad_spend ?? 0, 2),
        comparedValue: formatNumber(
          comparedData?.overall?.[nvrKey]?.return_on_ad_spend ?? 0,
          2
        ),
        compared: Boolean(comparedData),
        isIncreasing: Boolean(
          data?.overall?.[nvrKey].return_on_ad_spend &&
          comparedData?.overall?.[nvrKey]?.return_on_ad_spend &&
          data?.overall?.[nvrKey].return_on_ad_spend >
            comparedData?.overall?.[nvrKey]?.return_on_ad_spend
        ),
        rate:
          data?.overall?.[nvrKey].return_on_ad_spend &&
          comparedData?.overall?.[nvrKey]?.return_on_ad_spend
            ? getCorrelation(
                data?.overall?.[nvrKey].return_on_ad_spend,
                comparedData?.overall?.[nvrKey]?.return_on_ad_spend,
                2
              )
            : 0,
        dataAttributes: { display: "number" },
        tooltip: createTooltip("marketing_efficiency_ratio"),
      },
      {
        title: "CAC",
        value:
          data?.overall?.[nvrKey].customer_acquisition_cost ||
          comparedData?.overall?.[nvrKey]?.customer_acquisition_cost
            ? `${calculateCurrencyConvertion(
                data?.overall?.[nvrKey].customer_acquisition_cost ?? 0,
                2
              )} `
            : "-",
        comparedValue: comparedData?.overall?.[nvrKey]
          ?.customer_acquisition_cost
          ? `${calculateCurrencyConvertion(
              comparedData?.overall?.[nvrKey]?.customer_acquisition_cost ?? 0,
              2
            )} `
          : "-",
        compared: Boolean(comparedData),
        isIncreasing: Boolean(
          data?.overall?.[nvrKey].customer_acquisition_cost &&
          comparedData?.overall?.[nvrKey]?.customer_acquisition_cost &&
          data?.overall?.[nvrKey].customer_acquisition_cost <
            comparedData?.overall?.[nvrKey]?.customer_acquisition_cost
        ),
        rate: getCorrelation(
          data?.overall?.[nvrKey].customer_acquisition_cost ?? 0,
          comparedData?.overall?.[nvrKey]?.customer_acquisition_cost ?? 0,
          2
        ),
        dataAttributes: { display: "currency" },
        tooltip: createTooltip("customer_acquisition_cost"),
      },
      {
        title: "New Customer Rate",
        value:
          data?.overall?.[nvrKey].new_customer_purchase_ratio ||
          comparedData?.overall?.[nvrKey]?.new_customer_purchase_ratio
            ? `${formatNumber(
                (data?.overall?.[nvrKey].new_customer_purchase_ratio ?? 0) *
                  100,
                2
              )}%`
            : "-",
        comparedValue: comparedData?.overall?.[nvrKey]
          ?.new_customer_purchase_ratio
          ? `${formatNumber(
              (comparedData?.overall?.[nvrKey]?.new_customer_purchase_ratio ??
                0) * 100,
              2
            )}%`
          : "-",
        compared: Boolean(comparedData),
        isIncreasing: Boolean(
          data?.overall?.[nvrKey].new_customer_purchase_ratio &&
          comparedData?.overall?.[nvrKey]?.new_customer_purchase_ratio &&
          data?.overall?.[nvrKey].new_customer_purchase_ratio >
            comparedData?.overall?.[nvrKey]?.new_customer_purchase_ratio
        ),
        rate: getCorrelation(
          data?.overall?.[nvrKey].new_customer_purchase_ratio ?? 0,
          comparedData?.overall?.[nvrKey]?.new_customer_purchase_ratio,
          4
        ),
        tooltip: createTooltip("new_customer_purchase_ratio"),
      },
    ];

    return cards;
  }, [
    calculateCurrencyConvertion,
    data?.overall,
    nvrKey,
    comparedData,
    dashboardMode,
    createTooltip,
    formatNumber,
  ]);

  return (
    <div className="gap-3 lg:gap-4 flex flex-col">
      <div className={`grid grid-cols-12 items-stretch gap-2`}>
        {loading
          ? Array(6)
              .fill(1)
              .map((el, index) => {
                return (
                  <div
                    className="col-span-6 lg:col-span-4 2xl:col-span-2"
                    key={`skeleton${index}`}
                  >
                    <Card
                      className={cn(
                        "h-full flex flex-col lg:flex-row lg:items-center items-start lg:justify-between",
                        "p-4"
                      )}
                    >
                      <div className="flex h-full flex-col items-start lg:order-1">
                        <CardHeader className={cn("p-0 pb-2")}>
                          <CardTitle
                            className={cn(
                              "text-sm lg:text-base text-foreground font-medium wrap-break-word flex items-center"
                            )}
                          >
                            <Skeleton className="h-4 w-24 bg-muted" />
                          </CardTitle>
                        </CardHeader>
                        <CardContent className={cn("p-0 flex-1")}>
                          <Skeleton
                            className={cn(
                              "text-lg lg:text-2xl",
                              "h-7 lg:h-8 w-20 mt-2 bg-muted"
                            )}
                          />
                        </CardContent>
                      </div>
                    </Card>
                  </div>
                );
              })
          : null}
        {!loading &&
          performanceCardsData &&
          performanceCardsData.map((card: PerformanceCardInterface, index) => (
            <div
              className="col-span-6 lg:col-span-4 2xl:col-span-2"
              key={`card${index}`}
            >
              <PerformanceCard {...card} chartData={undefined} isReduced />
            </div>
          ))}
      </div>
      <RevenueInfoContainer
        historicalData={getGranularityData(data, granularity)}
        historicalChannelData={
          channelData?.channels
            ? getGranularityChannelData(channelData.channels, granularity)
            : {}
        }
        historicalComparedData={
          comparedData ? getGranularityData(comparedData, granularity) : []
        }
        historicalComparedChannelData={
          comparedChannelData?.channels
            ? getGranularityChannelData(
                comparedChannelData.channels,
                granularity
              )
            : {}
        }
        loading={loading}
        granularity={granularity}
      />
      <CACDevelopmentChart
        historicalData={getGranularityData(data, granularity)}
        historicalComparedData={
          comparedData ? getGranularityData(comparedData, granularity) : []
        }
        loading={loading}
        granularity={granularity}
      />
    </div>
  );
}
